import { cn } from "@/lib/utils";
import React, { useId } from "react";

export const UnderlinedWord = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const id = useId();
  const gradientId = `ul-grad-${id}`;
  const filterId = `ul-glow-${id}`;

  return (
    <span className="relative inline-block">
      {children}
      <span className="absolute left-0 right-0 -bottom-1 sm:-bottom-2 h-2 sm:h-3">
        {/* Inline glow */}
        <span
          className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-4 blur-lg opacity-40 animate-glow-pulse bg-brand-accent"
        />
        <svg
          viewBox="0 0 240 10"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="absolute inset-0 w-full h-full"
          preserveAspectRatio="xMidYMid meet"
        >
          <defs>
            <linearGradient
              id={gradientId}
              x1="0%"
              y1="0%"
              x2="100%"
              y2="0%"
            >
              <stop offset="0%" stopColor="var(--brand-accent)" stopOpacity="0.15" />
              <stop offset="15%" stopColor="var(--brand-accent)" stopOpacity="1" />
              <stop offset="50%" stopColor="var(--brand-accent)" />
              <stop offset="85%" stopColor="var(--brand-accent)" stopOpacity="1" />
              <stop offset="100%" stopColor="var(--brand-accent)" stopOpacity="0.15" />
            </linearGradient>
            <filter id={filterId}>
              <feGaussianBlur stdDeviation="1" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          <path
            d="M 5 4 Q 120 8 235 4"
            stroke={`url(#${gradientId})`}
            strokeWidth="2.5"
            strokeLinecap="round"
            fill="none"
            filter={`url(#${filterId})`}
          />
        </svg>
      </span>
    </span>
  );
};

const GradientLine = ({ className }: { className?: string }) => {
  const id = useId();
  const gradientId = `gl-grad-${id}`;
  const filterId = `gl-glow-${id}`;

  return (
    <div className={cn("flex justify-center", className)}>
      <div className="relative h-3 w-48 sm:w-64">
        {/* Diffused glow behind */}
        <div
          className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-5 blur-lg opacity-50 animate-glow-pulse"
          style={{
            background:
              "linear-gradient(90deg, transparent, var(--brand-accent), var(--brand-accent), var(--brand-accent), transparent)",
          }}
        />

        {/* SVG near-flat line with tiny sag */}
        <svg
          viewBox="0 0 240 10"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="absolute inset-0 w-full h-full"
          preserveAspectRatio="xMidYMid meet"
        >
          <defs>
            <linearGradient
              id={gradientId}
              x1="0%"
              y1="0%"
              x2="100%"
              y2="0%"
            >
              <stop offset="0%" stopColor="var(--brand-accent)" stopOpacity="0.15" />
              <stop offset="15%" stopColor="var(--brand-accent)" stopOpacity="1" />
              <stop offset="50%" stopColor="var(--brand-accent)" />
              <stop offset="85%" stopColor="var(--brand-accent)" stopOpacity="1" />
              <stop offset="100%" stopColor="var(--brand-accent)" stopOpacity="0.15" />
            </linearGradient>
            <filter id={filterId}>
              <feGaussianBlur stdDeviation="1" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          <path
            d="M 5 4 Q 120 8 235 4"
            stroke={`url(#${gradientId})`}
            strokeWidth="2.5"
            strokeLinecap="round"
            fill="none"
            filter={`url(#${filterId})`}
          />
        </svg>
      </div>
    </div>
  );
};

export default GradientLine;
